import { useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { fileUrl, formatApiError, uploadFile } from "@/lib/api";
import { OFERTA_ESTADO } from "@/lib/oferta";
import { RichTextEditor } from "@/components/admin/RichTextEditor";

export const OFERTA_EMPTY = {
  titulo: "", descripcion_html: "", imagen: null, precio_lista: "", precio_oferta: "",
  mensualidad_desde: "", enganche_desde: "", vigencia_inicio: "", vigencia_fin: "", condiciones: "", activa: false,
};
const NUM = ["precio_lista", "precio_oferta", "mensualidad_desde", "enganche_desde"];

export function ofertaFromItem(o) {
  if (!o) return { ...OFERTA_EMPTY };
  const f = { ...OFERTA_EMPTY };
  Object.keys(OFERTA_EMPTY).forEach((k) => { if (o[k] !== null && o[k] !== undefined) f[k] = NUM.includes(k) ? String(o[k]) : o[k]; });
  return f;
}

export function ofertaToPayload(f) {
  const p = { ...f };
  NUM.forEach((k) => { p[k] = f[k] === "" ? null : Number(f[k]); });
  ["titulo", "descripcion_html", "vigencia_inicio", "vigencia_fin", "condiciones"].forEach((k) => { p[k] = (f[k] || "").trim() || null; });
  return p;
}

export function OfertaBadge({ estado, testId }) {
  const e = OFERTA_ESTADO[estado];
  if (!e) return <span className="text-xs text-[#6B7678]" data-testid={testId}>—</span>;
  return <span className={`inline-block rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-wider ${e.cls}`} data-testid={testId}>{e.label}</span>;
}

export function OfertaEditor({ value, onChange, estado, category, unitPrice, onUploading }) {
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const fechaMal = value.vigencia_inicio && value.vigencia_fin && value.vigencia_fin < value.vigencia_inicio;

  async function onImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); onUploading?.(true);
    try { set("imagen", await uploadFile(file, `catalog/${category}/ofertas`)); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); onUploading?.(false); e.target.value = ""; }
  }

  return (
    <fieldset className="rounded-sm border border-[#C4674F]/40 bg-[#C4674F]/5 p-4" data-testid="oferta-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <legend className="font-serif text-xl text-[#3E5641]">Oferta comercial</legend>
        <div className="flex items-center gap-3">
          <OfertaBadge estado={estado} testId="oferta-estado" />
          <label className="flex items-center gap-2 text-sm text-[#3E5641]">
            Oferta activa <Switch checked={!!value.activa} onCheckedChange={(v) => set("activa", v)} data-testid="oferta-activa" />
          </label>
        </div>
      </div>
      <p className="mt-1 text-xs text-[#6B7678]">Todos los campos son opcionales. Solo se muestra en el sitio si está activa y dentro de su vigencia.</p>
      {estado === "vencida" && <p className="mt-2 rounded-sm bg-red-50 p-2 text-xs text-red-700" data-testid="oferta-vencida-aviso">Esta oferta está vencida y ya no se ve en el sitio. Cambia la fecha de fin para renovarla.</p>}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2"><Label className="field-label">Título de la oferta</Label><Input value={value.titulo} onChange={(e) => set("titulo", e.target.value)} data-testid="oferta-titulo" /></div>
        <div><Label className="field-label">Precio de lista</Label><Input type="number" min="0" value={value.precio_lista} placeholder={unitPrice ? `Vacío = ${unitPrice}` : ""} onChange={(e) => set("precio_lista", e.target.value)} data-testid="oferta-precio-lista" /></div>
        <div><Label className="field-label">Precio con oferta</Label><Input type="number" min="0" value={value.precio_oferta} onChange={(e) => set("precio_oferta", e.target.value)} data-testid="oferta-precio-oferta" /></div>
        <div><Label className="field-label">Mensualidad desde</Label><Input type="number" min="0" value={value.mensualidad_desde} onChange={(e) => set("mensualidad_desde", e.target.value)} data-testid="oferta-mensualidad" /></div>
        <div><Label className="field-label">Enganche desde</Label><Input type="number" min="0" value={value.enganche_desde} onChange={(e) => set("enganche_desde", e.target.value)} data-testid="oferta-enganche" /></div>
        <div><Label className="field-label">Vigencia: inicio</Label><Input type="date" value={value.vigencia_inicio} onChange={(e) => set("vigencia_inicio", e.target.value)} data-testid="oferta-vig-inicio" /></div>
        <div><Label className="field-label">Vigencia: fin</Label><Input type="date" value={value.vigencia_fin} onChange={(e) => set("vigencia_fin", e.target.value)} data-testid="oferta-vig-fin" /></div>
        {fechaMal && <p className="md:col-span-2 text-xs text-red-600" data-testid="oferta-fecha-error">La fecha de fin no puede ser anterior a la de inicio.</p>}
      </div>
      <div className="mt-4"><Label className="field-label">Descripción</Label><RichTextEditor value={value.descripcion_html} onChange={(v) => set("descripcion_html", v)} testId="oferta-descripcion" /></div>
      <div className="mt-4">
        <Label className="field-label">Imagen o cartel de la oferta</Label>
        <div className="flex items-center gap-4">
          {value.imagen && (
            <div className="relative">
              <img src={fileUrl(value.imagen)} alt="" className="h-24 w-32 object-cover border border-[#3E5641]/10" data-testid="oferta-imagen-preview" />
              <button type="button" onClick={() => set("imagen", null)} className="absolute right-1 top-1 rounded-sm bg-white/95 p-1" aria-label="Quitar imagen" data-testid="oferta-imagen-quitar"><X size={10} /></button>
            </div>
          )}
          <label className="inline-flex cursor-pointer items-center gap-2 border border-dashed border-[#3E5641] px-4 py-3 text-sm">
            <Upload size={14} /> {uploading ? "Subiendo..." : value.imagen ? "Cambiar" : "Subir imagen"}
            <input type="file" hidden accept="image/*" onChange={onImage} data-testid="oferta-imagen-input" />
          </label>
        </div>
      </div>
      <div className="mt-4"><Label className="field-label">Condiciones / letra chica</Label><Textarea rows={3} value={value.condiciones} onChange={(e) => set("condiciones", e.target.value)} data-testid="oferta-condiciones" /></div>
    </fieldset>
  );
}
